import axios from 'axios';
import { refreshTokenApi } from './index';
import userService from '../../user.service';
import { navigateToLogin } from '../navigation';

// Create axios instance
const api = axios.create({
  baseURL: process.env.REACT_APP_EXPRESS_URL,
  headers: {
    'Content-Type': 'application/json',
    Platform: 'web',
  },
});

// Create a source for cancelling requests on logout
let cancelTokenSource = axios.CancelToken.source();

// Paths that don't require authentication
const noAuthPaths = [
  '/api/users/login',
  '/api/users/reset',
  '/api/canCare/signupFromPortal',
  '/api/canCare/resendOtp',
  '/api/canCare/verifyOtp',
  '/api/canCare/signupByInvite',
  '/api/canCare/getTokenData',
];

// Track if refresh is in progress
let isRefreshing = false;
let failedQueue = [];
let logoutInProgress = false;

// Circuit breaker for refresh attempts
let refreshAttempts = 0;
let lastRefreshAttempt = 0;
const MAX_REFRESH_ATTEMPTS = 3;
const REFRESH_COOLDOWN = 60000; // 1 minute

// Debug: Track request counts to detect loops
let requestCount = 0;
let requestTracker = new Map();

const trackRequest = (url) => {
  requestCount++;
  const count = requestTracker.get(url) || 0;
  requestTracker.set(url, count + 1);

  if (count > 10) {
    console.error('🚨 POTENTIAL INFINITE LOOP DETECTED:', url, 'called', count + 1, 'times');
  }

  // Reset counter every 30 seconds
  if (requestCount % 100 === 0) {
    console.log('🔍 Request tracker reset, total requests:', requestCount);
    requestTracker.clear();
  }
};

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Function to get auth token consistently
const getAuthToken = () => {
  // First try the new storage keys
  let token = localStorage.getItem('auth') || sessionStorage.getItem('token');

  // Fallback to old LoopBack keys for backward compatibility
  if (!token || token === 'null' || token === 'undefined') {
    token = localStorage.getItem('$LoopBack$accessTokenId') || sessionStorage.getItem('$LoopBack$accessTokenId');
  }

  // Handle the token suffix that login adds (~29#215@rfeqwEt0)
  if (token && token.includes('~29#215@rfeqwEt0')) {
    token = token.replace('~29#215@rfeqwEt0', '');
  }

  // Return null if we get the string 'null' or other falsy values
  return token && token !== 'null' && token !== 'undefined' ? token : null;
};

// Function to get user ID consistently
const getUserId = () => {
  const userId =
    localStorage.getItem('userId') ||
    localStorage.getItem('$LoopBack$currentUserId') ||
    sessionStorage.getItem('$LoopBack$currentUserId');

  // Return null if we get the string 'null' or other falsy values
  return userId && userId !== 'null' && userId !== 'undefined' ? userId : null;
};

// Function to get refresh token consistently
const getRefreshToken = () => {
  const refreshToken = localStorage.getItem('refreshToken') || sessionStorage.getItem('refreshToken');

  // Return null if we get the string 'null' or other falsy values
  return refreshToken && refreshToken !== 'null' && refreshToken !== 'undefined' ? refreshToken : null;
};

// Function to handle logout
const handleLogout = () => {
  console.log('🚪 handleLogout called, logoutInProgress:', logoutInProgress);

  // Prevent multiple simultaneous logout calls
  if (logoutInProgress) {
    console.log('🚪 Logout already in progress, ignoring');
    return;
  }
  logoutInProgress = true;

  console.log('🚫 Cancelling all pending requests');
  // Cancel all pending requests
  cancelTokenSource.cancel('Authentication failed - logging out');
  cancelTokenSource = axios.CancelToken.source();

  console.log('🧹 Clearing storage and user data');
  // Clear all storage
  localStorage.clear();
  sessionStorage.clear();
  userService.clearUserData();

  // Reset flags
  isRefreshing = false;
  failedQueue = [];

  console.log('🔄 Scheduling navigation to login');
  // Use setTimeout to avoid immediate redirect during request processing
  setTimeout(() => {
    console.log('🏠 Navigating to login page');
    // Use hash navigation to avoid page refresh
    navigateToLogin();
    logoutInProgress = false;
    console.log('✅ Logout process completed');
  }, 100);
};

// Request interceptor
api.interceptors.request.use((config) => {
  const isNoAuthPath = noAuthPaths.some((path) => config.url.includes(path));
  config.headers['ngrok-skip-browser-warning'] = 'true';
  // Track this request
  trackRequest(config.url);

  console.log('🔵 REQUEST:', {
    url: config.url,
    method: config.method,
    isNoAuthPath,
    isRefreshing,
    logoutInProgress,
    hasRetryFlag: !!config._retry,
    requestCount: requestTracker.get(config.url) || 1,
  });

  config.headers.Platform = 'web';

  // Add cancel token to all requests
  config.cancelToken = cancelTokenSource.token;

  if (!isNoAuthPath) {
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = token;
      console.log('🔑 Added auth token to request:', token.substring(0, 20) + '...');
    } else {
      console.log('❌ No auth token found for protected route');
      console.log('🔍 Token storage debug:', {
        localStorage_auth: localStorage.getItem('auth'),
        localStorage_LoopBack: localStorage.getItem('$LoopBack$accessTokenId'),
        sessionStorage_token: sessionStorage.getItem('token'),
        sessionStorage_LoopBack: sessionStorage.getItem('$LoopBack$accessTokenId'),
        allLocalStorage: Object.keys(localStorage).filter(
          (key) => key.includes('auth') || key.includes('token') || key.includes('LoopBack'),
        ),
        allSessionStorage: Object.keys(sessionStorage).filter(
          (key) => key.includes('auth') || key.includes('token') || key.includes('LoopBack'),
        ),
      });

      // If no token found, this should trigger logout immediately
      console.log('🚪 No token found - should trigger logout');
      setTimeout(() => {
        if (!logoutInProgress) {
          console.log('🚪 Triggering logout due to missing token');
          handleLogout();
        }
      }, 100);
    }
  }

  return config;
});

// Response interceptor
api.interceptors.response.use(
  (response) => {
    console.log('✅ RESPONSE SUCCESS:', {
      url: response.config.url,
      status: response.status,
    });
    return response;
  },
  async (error) => {
    console.log('❌ RESPONSE ERROR:', {
      url: error.config?.url,
      status: error.response?.status,
      message: error.message,
      isCancel: axios.isCancel(error),
      logoutInProgress,
      isRefreshing,
    });

    // Handle cancelled requests
    if (axios.isCancel(error)) {
      console.log('🚫 Request was cancelled');
      return Promise.reject(error);
    }

    // If logout is in progress, reject all requests
    if (logoutInProgress) {
      console.log('🚪 Logout in progress - rejecting request');
      return Promise.reject(new Error('Logout in progress'));
    }

    const originalRequest = error.config;

    // If 401 and not a retry and not already refreshing
    if (error.response?.status === 401 && !originalRequest._retry) {
      console.log('🔴 401 UNAUTHORIZED detected:', {
        url: originalRequest.url,
        method: originalRequest.method,
        errorMessage: error.response?.data?.message || error.message,
        hasRetryFlag: !!originalRequest._retry,
      });

      originalRequest._retry = true;

      const userId = getUserId();
      const token = getAuthToken();
      const refreshToken = getRefreshToken();

      console.log('🔄 Token refresh attempt:', {
        userId: userId ? `${userId.substring(0, 8)}...` : 'null',
        hasToken: !!token,
        hasRefreshToken: !!refreshToken,
        tokenExpiry: 'Backend expired TTL',
      });

      // Check if we have valid credentials
      if (!userId || !token || !refreshToken) {
        console.log('❌ Missing credentials for token refresh - logging out');
        handleLogout();
        return Promise.reject(error);
      }

      // Circuit breaker: Check if we've exceeded refresh attempts
      const now = Date.now();
      if (now - lastRefreshAttempt > REFRESH_COOLDOWN) {
        refreshAttempts = 0; // Reset counter after cooldown
      }

      if (refreshAttempts >= MAX_REFRESH_ATTEMPTS) {
        console.log('🚫 Max refresh attempts exceeded - logging out');
        handleLogout();
        return Promise.reject(new Error('Max refresh attempts exceeded'));
      }

      if (isRefreshing) {
        console.log('⏳ Already refreshing - queueing request');
        // Queue requests until refresh completes
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((newToken) => {
            console.log('🔄 Retrying queued request with new token');
            originalRequest.headers.Authorization = newToken;
            originalRequest.cancelToken = cancelTokenSource.token; // Update cancel token
            return api(originalRequest);
          })
          .catch((err) => {
            console.log('❌ Queued request failed:', err.message);
            return Promise.reject(err);
          });
      }

      console.log('🔄 Starting token refresh...');
      isRefreshing = true;
      refreshAttempts++;
      lastRefreshAttempt = now;

      try {
        const response = await refreshTokenApi({ userId, token, refreshToken });
        console.log('✅ Token refresh response:', response);

        const newToken = response?.data?.token;
        if (!newToken) {
          throw new Error('No new token returned');
        }

        console.log('💾 Saving new token to storage');
        // Save new token to both storage methods for consistency
        // Add the same suffix that login uses
        localStorage.setItem('auth', newToken + '~29#215@rfeqwEt0');
        sessionStorage.setItem('token', newToken);

        if (localStorage.getItem('$LoopBack$accessTokenId')) {
          localStorage.setItem('$LoopBack$accessTokenId', newToken + '~29#215@rfeqwEt0');
        }
        if (sessionStorage.getItem('$LoopBack$accessTokenId')) {
          sessionStorage.setItem('$LoopBack$accessTokenId', newToken);
        }

        console.log('🔄 Processing queued requests');
        processQueue(null, newToken);

        // Reset refresh attempts on successful refresh
        refreshAttempts = 0;

        console.log('🔄 Retrying original request');
        originalRequest.headers.Authorization = newToken;
        originalRequest.cancelToken = cancelTokenSource.token; // Update cancel token
        return api(originalRequest);
      } catch (err) {
        console.log('❌ Token refresh failed:', err.message);
        processQueue(err, null);
        handleLogout();
        return Promise.reject(err);
      } finally {
        console.log('🏁 Token refresh process completed');
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);

// Export utility functions
export const resetCancelToken = () => {
  cancelTokenSource = axios.CancelToken.source();
};

export const forceLogout = handleLogout;

// Debug function to check current state
export const getDebugInfo = () => ({
  isRefreshing,
  logoutInProgress,
  failedQueueLength: failedQueue.length,
  hasAuthToken: !!getAuthToken(),
  hasUserId: !!getUserId(),
});

export default api;
